import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Plus, Search, X, Contact } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const STATUS_STYLES = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300",
  inactive: "bg-slate-100 text-slate-500 dark:bg-ink-panel dark:text-slate-400",
};
const TYPE_STYLES = {
  B2B: "bg-sky-50 text-sky-700 dark:bg-ink-gold/10 dark:text-ink-gold",
  B2C: "bg-slate-100 text-slate-600 dark:bg-ink-panel dark:text-slate-300",
};

export function displayName(c) {
  return c.customer_type === "B2B"
    ? (c.brand_name || c.legal_name || c.customer_id)
    : (c.preferred_name || c.full_legal_name || c.customer_id);
}

const B2B_FIELDS = [
  ["legal_name", "Legal / Company Name", true],
  ["brand_name", "Brand / Trading Name"],
  ["primary_contact", "Primary Contact"],
  ["website", "Website"],
  ["country", "Country"],
];
const B2C_FIELDS = [
  ["full_legal_name", "Full Legal Name", true],
  ["preferred_name", "Preferred Name"],
  ["nationality", "Nationality"],
  ["country_of_residence", "Country of Residence"],
  ["date_of_birth", "Date of Birth", false, "date"],
];
const SHARED_FIELDS = [
  ["email", "Email", false, "email"],
  ["phone", "Phone"],
  ["whatsapp", "WhatsApp"],
  ["address", "Address"],
];
const EMPTY_FORM = {
  customer_type: "B2B", status: "active",
  legal_name: "", brand_name: "", primary_contact: "", website: "", country: "",
  full_legal_name: "", preferred_name: "", nationality: "", country_of_residence: "", date_of_birth: "",
  email: "", phone: "", whatsapp: "", address: "", notes: "",
};

const INPUT_CLS = "w-full rounded-lg border border-slate-200 px-3 py-2 text-sm dark:border-ink-line dark:bg-ink-panel dark:text-ink-text";
const LABEL_CLS = "mb-1.5 block text-xs font-medium uppercase tracking-wider text-slate-400";

export default function Customers() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission("customers.create");
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [modal, setModal] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (typeFilter) params.type = typeFilter;
      if (statusFilter) params.status = statusFilter;
      const r = await api.get("/customers", { params });
      setRows(r.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, typeFilter, statusFilter]);

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slate-400">CRM</p>
          <h1 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-ink-text">Customers</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">B2B customer organizations and B2C individual customers.</p>
        </div>
        {canCreate && (
          <button onClick={() => setModal({ mode: "create", customer: { ...EMPTY_FORM } })} data-testid="add-customer-button" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
            <Plus className="h-4 w-4" /> Add customer
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, ID, email, phone…" data-testid="customer-search-input" className={`${INPUT_CLS} pl-9`} />
        </div>
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="customer-type-filter" className={`${INPUT_CLS} w-auto`}>
          <option value="">All types</option>
          <option value="B2B">B2B</option>
          <option value="B2C">B2C</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="customer-status-filter" className={`${INPUT_CLS} w-auto`}>
          <option value="">All statuses</option>
          <option value="active">active</option>
          <option value="inactive">inactive</option>
        </select>
      </div>

      {loading ? (
        <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-sky-600 dark:text-ink-gold" /></div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center dark:border-ink-line" data-testid="customers-empty-state">
          <Contact className="mx-auto h-8 w-8 text-slate-400" />
          <p className="mt-3 text-sm font-medium text-slate-600 dark:text-slate-300">No customers found</p>
          <p className="mt-1 text-xs text-slate-400">Create your first B2B or B2C customer to start building relationships.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-ink-line dark:bg-ink-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="customers-table">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500 dark:border-ink-line dark:bg-ink/40">
                  <th className="px-5 py-3 font-medium">Customer ID</th>
                  <th className="px-5 py-3 font-medium">Name</th>
                  <th className="px-5 py-3 font-medium">Type</th>
                  <th className="px-5 py-3 font-medium">Contact</th>
                  <th className="px-5 py-3 font-medium">Country</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/customers/${c.id}`)} className="cursor-pointer border-b border-slate-100 last:border-0 hover:bg-slate-50 dark:border-ink-line dark:hover:bg-ink-panel/40" data-testid={`customer-row-${c.customer_id}`}>
                    <td className="px-5 py-3 font-mono-tf text-xs font-semibold text-slate-700 dark:text-ink-gold">{c.customer_id}</td>
                    <td className="px-5 py-3 font-medium text-slate-800 dark:text-ink-text">{displayName(c)}</td>
                    <td className="px-5 py-3"><span className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${TYPE_STYLES[c.customer_type]}`} data-testid={`customer-type-${c.customer_id}`}>{c.customer_type}</span></td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">
                      <div className="text-xs">{c.email || "—"}</div>
                      <div className="text-[11px] text-slate-400">{c.phone || ""}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-600 dark:text-slate-300">{c.country || c.country_of_residence || "—"}</td>
                    <td className="px-5 py-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLES[c.status] || STATUS_STYLES.inactive}`} data-testid={`customer-status-${c.customer_id}`}>{c.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {modal && <CustomerModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

export function CustomerModal({ modal, onClose, onSaved }) {
  const isCreate = modal.mode === "create";
  const [form, setForm] = useState({ ...EMPTY_FORM, ...modal.customer });
  const [saving, setSaving] = useState(false);
  const typeFields = form.customer_type === "B2B" ? B2B_FIELDS : B2C_FIELDS;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isCreate) {
        const payload = { customer_type: form.customer_type, status: form.status };
        Object.keys(EMPTY_FORM).forEach((k) => {
          if (!["customer_type", "status"].includes(k) && String(form[k] ?? "").trim() !== "") payload[k] = form[k].trim();
        });
        const r = await api.post("/customers", payload);
        toast.success(`Customer ${r.data.customer_id} created`);
      } else {
        const payload = {};
        Object.keys(EMPTY_FORM).forEach((k) => {
          if (k === "customer_type") return;
          const v = form[k] ?? "";
          const orig = modal.customer[k] ?? "";
          if (v !== orig) payload[k] = v === "" ? "" : v;
        });
        if (Object.keys(payload).length === 0) { onClose(); return; }
        await api.patch(`/customers/${modal.customer.id}`, payload);
        toast.success("Customer updated");
      }
      onSaved();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const renderField = ([key, label, required, type]) => (
    <div key={key}>
      <label className={LABEL_CLS}>{label}{required ? " *" : ""}</label>
      <input type={type || "text"} required={required} value={form[key] ?? ""} onChange={set(key)} data-testid={`customer-${key.replace(/_/g, "-")}-input`} className={INPUT_CLS} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="customer-modal">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl rounded-xl border border-slate-200 bg-white shadow-2xl dark:border-ink-line dark:bg-ink-card">
        <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4 dark:border-ink-line">
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-ink-text">
            {isCreate ? "Add Customer" : `Edit ${modal.customer.customer_id}`}
          </h3>
          <button onClick={onClose} data-testid="customer-modal-close" className="rounded-md p-1 text-slate-400 hover:bg-slate-100 dark:hover:bg-ink-panel"><X className="h-5 w-5" /></button>
        </div>
        <form onSubmit={submit} className="max-h-[70vh] space-y-4 overflow-y-auto px-6 py-5">
          <div>
            <label className={LABEL_CLS}>Customer Type</label>
            {isCreate ? (
              <div className="grid grid-cols-2 gap-2" data-testid="customer-type-picker">
                {["B2B", "B2C"].map((t) => (
                  <button type="button" key={t} onClick={() => setForm((f) => ({ ...f, customer_type: t }))} data-testid={`customer-type-${t.toLowerCase()}`}
                    className={`rounded-lg border px-3 py-2 text-sm font-semibold ${form.customer_type === t ? "border-sky-300 bg-sky-50 text-sky-700 dark:border-ink-gold/40 dark:bg-ink-gold/10 dark:text-ink-gold" : "border-slate-200 text-slate-500 dark:border-ink-line dark:text-slate-400"}`}>
                    {t === "B2B" ? "B2B — Organization" : "B2C — Individual"}
                  </button>
                ))}
              </div>
            ) : (
              <span className={`inline-block rounded px-2 py-1 text-xs font-semibold ${TYPE_STYLES[form.customer_type]}`} data-testid="customer-type-readonly">{form.customer_type} (locked)</span>
            )}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {typeFields.map(renderField)}
            {SHARED_FIELDS.map(renderField)}
          </div>
          <div>
            <label className={LABEL_CLS}>Notes</label>
            <textarea rows={2} value={form.notes ?? ""} onChange={set("notes")} data-testid="customer-notes-input" className={INPUT_CLS} />
          </div>
          <div>
            <label className={LABEL_CLS}>Status</label>
            <select value={form.status} onChange={set("status")} data-testid="customer-status-select" className={INPUT_CLS}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 dark:border-ink-line dark:text-slate-300">Cancel</button>
            <button type="submit" disabled={saving} data-testid="customer-modal-save" className="flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-500 disabled:opacity-60 dark:bg-ink-gold dark:text-[#0B1120] dark:hover:bg-ink-golddeep">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {isCreate ? "Create" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
